import { setDoc, doc, getDoc, updateDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, UserPublicProfile, UserPrivateData } from '../types';
import { logger } from '../utils/secureLogger';

/**
 * セキュアなユーザー管理サービス
 * 公開情報と機密情報を分離したDB構造を管理
 */
export class UserService {
  private static instance: UserService;

  private constructor() {}

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * 新規ユーザーを作成（セキュア構造）
   * トランザクションを使用して既存ユーザーの上書きを防止
   */
  async createUser(userData: User): Promise<User> {
    if (!db) {
      throw new Error('Database not available');
    }

    try {
      const now = new Date();

      // トランザクションで既存チェックと作成を原子的に実行
      await runTransaction(db, async (transaction) => {
        const publicDocRef = doc(db, 'users_public', userData.id);
        const privateDocRef = doc(db, 'users_private', userData.id);

        // 既存チェック：どちらかが存在したら即座に中止
        const [publicDoc, privateDoc] = await Promise.all([
          transaction.get(publicDocRef),
          transaction.get(privateDocRef)
        ]);

        if (publicDoc.exists() || privateDoc.exists()) {
          throw new Error(`User already exists: ${userData.id}. Aborting to prevent data overwrite.`);
        }

        // 公開プロフィール情報をusers_publicに保存
        const publicProfile: UserPublicProfile = {
          id: userData.id,
          displayName: userData.displayName || '',
          photoURL: userData.photoURL || '',
          createdAt: now,
          updatedAt: now
        };

        // 機密情報をusers_privateに保存
        const privateData: UserPrivateData = {
          id: userData.id,
          email: userData.email || '',
          createdAt: now,
          updatedAt: now
        };

        // undefinedを除去してFirestoreに保存
        const cleanPublicProfile = Object.fromEntries(
          Object.entries({
            ...publicProfile,
            createdAt: publicProfile.createdAt.toISOString(),
            updatedAt: publicProfile.updatedAt.toISOString()
          }).filter(([_, value]) => value !== undefined)
        );

        const cleanPrivateData = Object.fromEntries(
          Object.entries({
            ...privateData,
            createdAt: privateData.createdAt.toISOString(),
            updatedAt: privateData.updatedAt.toISOString()
          }).filter(([_, value]) => value !== undefined)
        );

        // トランザクション内で両方のドキュメントを作成
        transaction.set(publicDocRef, cleanPublicProfile);
        transaction.set(privateDocRef, cleanPrivateData);
      });

      logger.info('New user created with secure structure:', userData.id);
      return userData;

    } catch (error) {
      // 既存ユーザーエラーの場合は情報レベルでログ
      if (error instanceof Error && error.message.includes('User already exists')) {
        logger.info('User creation skipped - user already exists:', userData.id);
        throw error;
      }

      logger.error('Error creating user:', error);
      throw new Error(`Failed to create user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * ユーザー情報を取得（公開情報のみ）
   */
  async getUserPublicProfile(userId: string): Promise<UserPublicProfile | null> {
    if (!db) {
      throw new Error('Database not available');
    }

    try {
      const userDoc = await getDoc(doc(db, 'users_public', userId));
      
      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      return {
        id: userData.id,
        displayName: userData.displayName,
        photoURL: userData.photoURL,
        createdAt: new Date(userData.createdAt),
        updatedAt: new Date(userData.updatedAt)
      };

    } catch (error) {
      logger.error('Error fetching user public profile:', error);
      throw error;
    }
  }

  /**
   * 完全なユーザー情報を取得（認証済みユーザーのみ、自分の情報のみ）
   */
  async getUserFullData(userId: string, requestingUserId: string): Promise<User | null> {
    if (!db) {
      throw new Error('Database not available');
    }

    // 本人以外は機密情報にアクセスできない
    if (userId !== requestingUserId) {
      const publicProfile = await this.getUserPublicProfile(userId);
      if (!publicProfile) return null;
      
      // 公開情報のみを返す（emailなし）
      return {
        ...publicProfile,
        email: '' // 機密情報は隠す
      };
    }

    try {
      const [publicDoc, privateDoc] = await Promise.all([
        getDoc(doc(db, 'users_public', userId)),
        getDoc(doc(db, 'users_private', userId))
      ]);

      if (!publicDoc.exists() || !privateDoc.exists()) {
        return null;
      }

      const publicData = publicDoc.data();
      const privateData = privateDoc.data();

      return {
        id: publicData.id,
        displayName: publicData.displayName,
        photoURL: publicData.photoURL,
        email: privateData.email,
        createdAt: new Date(publicData.createdAt),
        updatedAt: new Date(publicData.updatedAt)
      };

    } catch (error) {
      logger.error('Error fetching user full data:', error);
      throw error;
    }
  }

  /**
   * ユーザー情報を更新
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    if (!db) {
      throw new Error('Database not available');
    }

    try {
      const now = new Date();
      const publicUpdates: Partial<UserPublicProfile> = {};
      const privateUpdates: Partial<UserPrivateData> = {};

      // フィールドを適切なコレクションに分類
      if (updates.displayName !== undefined) publicUpdates.displayName = updates.displayName;
      if (updates.photoURL !== undefined) publicUpdates.photoURL = updates.photoURL;

      if (updates.email !== undefined) privateUpdates.email = updates.email;

      // 更新日時を両方に追加
      publicUpdates.updatedAt = now;
      privateUpdates.updatedAt = now;

      const updatePromises = [];

      // 公開情報の更新
      if (Object.keys(publicUpdates).length > 1) { // updatedAt以外にも更新がある場合
        updatePromises.push(
          updateDoc(doc(db, 'users_public', userId), {
            ...publicUpdates,
            updatedAt: now.toISOString()
          })
        );
      }

      // 機密情報の更新
      if (Object.keys(privateUpdates).length > 1) { // updatedAt以外にも更新がある場合
        updatePromises.push(
          updateDoc(doc(db, 'users_private', userId), {
            ...privateUpdates,
            updatedAt: now.toISOString()
          })
        );
      }

      await Promise.all(updatePromises);
      logger.info('User updated with secure structure:', userId);

    } catch (error) {
      logger.error('Error updating user:', error);
      throw new Error(`Failed to update user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

}

// シングルトンインスタンスをエクスポート
export const userService = UserService.getInstance();